// Cuadro de respuesta de una conversacion (D40): Responder, Responder a todos, Reenviar y (en casos de Zammad) Nota
// interna, siempre sobre el mensaje elegido. Para y CC se pueden editar (quitar o agregar), con adjuntos, texto rapido
// y firma. El asistente puede dejar aqui su borrador ("Usar en la respuesta").
// D56: flota abajo sobre la conversacion (como en Zammad), crece con lo que se escribe, se estira desde el borde de
// arriba y guarda el borrador en el navegador hasta que se envia (si el envio falla o se recarga, sigue ahi).
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { useSession } from '../auth/session';
import { borrarBorradorRespuesta, guardarBorradorRespuesta, leerBorradorRespuesta } from '../lib/borradorRespuesta';
import { type DatosPlantilla, destinosDe, type ModoRespuesta } from '../lib/correo';
import { Icon } from '../lib/icons';
import { htmlAMarkdown } from '../lib/htmlAMarkdown';
import { markdownAHtml } from '../lib/markdownIA';
import { AVISO_ARTICULO, type DetalleUsar, registrarRespuesta, tomarPendiente } from '../lib/insertarEnRespuesta';
import { readJSON, writeJSON } from '../lib/storage';
import { type MsgConv } from './Conversacion';
import { CampoCorreos } from './CampoCorreos';
import { InsertarArticulo } from './InsertarArticulo';
import { TextoRapido } from './Plantillas';
import { toast } from './toasts';

export type Eleccion = { base: MsgConv | null; modo: ModoRespuesta; abrir?: number };
export type EnvioRespuesta = { modo: ModoRespuesta; para: string[]; cc: string[]; cco: string[]; texto: string; adjuntos: File[]; base: MsgConv | null; asunto: string };

// El asistente deja un borrador en el cuadro de respuesta abierto.
export const EVENTO_USAR_RESPUESTA = 'consola:usar-respuesta';
// Texto con Markdown (negrita, tabla, titulo o lista): el que conviene ver en el editor con formato.
const tieneFormato = (t: string) => /\*\*[^*\n]+\*\*|^\s*\|.*\|\s*$|^\s*#{1,4}\s|^\s*[-*•]\s+\S|^\s*\d+[.)]\s+\S/m.test(t);
export const usarEnRespuesta = (texto: string) => window.dispatchEvent(new CustomEvent(EVENTO_USAR_RESPUESTA, { detail: texto }));
// Un archivo que Amix arma (propuesta en PDF o Word, D93) entra como adjunto del cuadro de respuesta abierto.
export const EVENTO_ADJUNTAR_RESPUESTA = 'consola:adjuntar-respuesta';
export const adjuntarEnRespuesta = (archivo: File) => window.dispatchEvent(new CustomEvent(EVENTO_ADJUNTAR_RESPUESTA, { detail: archivo }));

const MAX_MB = 20;
const NOMBRE_MODO: Record<ModoRespuesta, string> = { responder: 'Responder', todos: 'Responder a todos', reenviar: 'Reenviar', nota: 'Nota interna' };
// Alto del texto: crece solo entre estos limites; si la persona lo estira, se recuerda (en px).
const CLAVE_ALTO = 'consola.respuesta.alto';
const MIN_ALTO = 140;
const maxAlto = () => Math.round(window.innerHeight * (window.innerWidth < 760 ? 0.5 : 0.6));

export function Respuesta({
  mios,
  sel,
  setSel,
  conNota,
  desde,
  avisoNota = 'Solo la ve el equipo.',
  paraInicial = [],
  datos,
  enviar,
  asuntoDe,
  conversacion,
  avisoCco,
  ccoNoDisponible,
  reasunto = 0,
}: {
  mios: string[];
  sel: Eleccion;
  setSel: (e: Eleccion) => void;
  conNota: boolean;
  desde: string;
  avisoNota?: string;
  paraInicial?: string[];
  datos: DatosPlantilla;
  enviar: (e: EnvioRespuesta) => Promise<void>;
  // Asunto propuesto para el mensaje y modo elegidos; se puede cambiar antes de enviar.
  asuntoDe: (base: MsgConv | null, modo: ModoRespuesta) => string;
  // Id del caso o del hilo: donde se guarda el borrador (D56).
  conversacion?: string;
  // Copia oculta (D60): aviso de como sale (p. ej. por Zammad, aparte) o por que no se puede.
  avisoCco?: string;
  ccoNoDisponible?: string;
  // Al cambiar, se vuelve a armar el asunto (p. ej. al pasar a responder desde el caso: lleva [Ticket#N]).
  reasunto?: number;
}) {
  const { yo } = useSession();
  const dueno = yo.miembroId ?? 'nadie';
  const guardado = useRef(conversacion ? leerBorradorRespuesta(dueno, conversacion) : null);
  const [asunto, setAsunto] = useState('');
  const [para, setPara] = useState<string[]>([]);
  const [cc, setCc] = useState<string[]>([]);
  const [verCc, setVerCc] = useState(false);
  const [cco, setCco] = useState<string[]>([]);
  const [texto, setTexto] = useState(guardado.current?.texto ?? '');
  // Editor con formato (D98): se ve como le llega al cliente (negrita, listas y tablas) y se edita ahi mismo; por debajo
  // el texto sigue en Markdown. Se abre solo cuando entra un texto con formato (AMIX, articulos o un borrador guardado).
  const conFormato = tieneFormato(texto);
  const [vista, setVista] = useState(conFormato);
  const [adjuntos, setAdjuntos] = useState<File[]>([]);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState('');
  // Plegado (D41): una sola linea hasta que se toca; asi la conversacion tiene la pantalla.
  const [abierto, setAbierto] = useState(false);
  const [alto, setAlto] = useState<number | null>(() => readJSON<number | null>(CLAVE_ALTO, null));
  const caja = useRef<HTMLTextAreaElement | null>(null);
  const editor = useRef<HTMLDivElement | null>(null);
  const archivo = useRef<HTMLInputElement | null>(null);
  const tarjeta = useRef<HTMLDivElement | null>(null);

  // Al elegir mensaje o modo se vuelven a armar Para y CC (lo escrito se conserva). La primera vez, si hay un borrador
  // guardado para este mismo modo, se usan sus destinatarios y su asunto.
  const clave = `${sel.base?.id ?? ''}|${sel.modo}|${paraInicial.join(',')}`;
  useEffect(() => {
    const b = guardado.current;
    guardado.current = null;
    if (b && b.modo === sel.modo && (b.para.length || sel.modo === 'nota')) {
      setPara(b.para);
      setCc(b.cc);
      setCco(b.cco ?? []);
      setVerCc(b.cc.length > 0 || (b.cco ?? []).length > 0);
      setAsunto(b.asunto || asuntoDe(sel.base, sel.modo));
      return;
    }
    const d = sel.base ? destinosDe(sel.base, mios, sel.modo) : { para: sel.modo === 'nota' ? [] : paraInicial, cc: [] };
    setPara(d.para);
    setCc(d.cc);
    setVerCc(d.cc.length > 0 || cco.length > 0);
    setAsunto(asuntoDe(sel.base, sel.modo));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clave]);
  useEffect(() => {
    if (reasunto) setAsunto(asuntoDe(sel.base, sel.modo));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reasunto]);
  // Una accion de un mensaje (Responder, A todos, Reenviar) abre el cuadro.
  useEffect(() => {
    if (sel.abrir) setAbierto(true);
  }, [sel.abrir]);
  useEffect(() => {
    if (abierto) caja.current?.focus();
  }, [abierto]);

  // Borrador: se guarda con una pausa corta mientras se escribe.
  useEffect(() => {
    if (!conversacion) return;
    const t = setTimeout(() => guardarBorradorRespuesta(dueno, conversacion, { texto, asunto, para, cc, cco, modo: sel.modo }), 400);
    return () => clearTimeout(t);
  }, [conversacion, dueno, texto, asunto, para, cc, cco, sel.modo]);

  // El texto crece con lo que se escribe (sin barra hasta el tope), salvo que la persona haya fijado un alto.
  useLayoutEffect(() => {
    const c = caja.current;
    if (!c || !abierto) return;
    if (alto) {
      c.style.height = `${Math.min(Math.max(alto, MIN_ALTO), maxAlto())}px`;
      return;
    }
    c.style.height = 'auto';
    c.style.height = `${Math.min(Math.max(c.scrollHeight + 2, MIN_ALTO), maxAlto())}px`;
  }, [texto, abierto, alto]);

  // La conversacion deja libre abajo el alto del cuadro, para que el ultimo mensaje no quede tapado.
  useLayoutEffect(() => {
    const el = tarjeta.current;
    const padre = el?.parentElement;
    if (!el || !padre) return;
    const poner = () => padre.style.setProperty('--resp-alto', `${el.offsetHeight + 24}px`);
    poner();
    const ro = new ResizeObserver(poner);
    ro.observe(el);
    return () => ro.disconnect();
  }, [abierto]);

  useEffect(() => {
    // El detalle es el texto (asistente) o { texto, aviso } (un articulo de la base de conocimiento).
    const usar = (e: Event) => {
      const d = (e as CustomEvent<DetalleUsar>).detail;
      const t = String((typeof d === 'object' && d ? d.texto : d) ?? '').trim();
      if (!t) return;
      setTexto((x) => (x.trim() ? `${x.replace(/\s+$/, '')}\n\n${t}` : t));
      if (tieneFormato(t)) setVista(true);
      setAbierto(true);
      toast(typeof d === 'object' && d ? d.aviso : 'El borrador del asistente quedó en la respuesta. Revísalo antes de enviar.');
    };
    window.addEventListener(EVENTO_USAR_RESPUESTA, usar);
    const adjuntar = (e: Event) => {
      const f = (e as CustomEvent<File>).detail;
      if (!(f instanceof File)) return;
      setAdjuntos((xs) => [...xs.filter((x) => x.name !== f.name), f]);
      setAbierto(true);
      toast(`${f.name} quedó adjunto en la respuesta. Revisa el texto y envía.`);
    };
    window.addEventListener(EVENTO_ADJUNTAR_RESPUESTA, adjuntar);
    const quitar = registrarRespuesta();
    const pendiente = tomarPendiente();
    if (pendiente) usar(new CustomEvent<DetalleUsar>(EVENTO_USAR_RESPUESTA, { detail: { texto: pendiente, aviso: AVISO_ARTICULO } }));
    return () => {
      quitar();
      window.removeEventListener(EVENTO_USAR_RESPUESTA, usar);
      window.removeEventListener(EVENTO_ADJUNTAR_RESPUESTA, adjuntar);
    };
  }, []);

  const nota = sel.modo === 'nota';
  const enEditor = vista && !nota;
  // El editor se llena desde el texto cuando cambia por fuera (AMIX, texto rapido, al abrirlo); lo que se escribe en el
  // editor ya viene de ahi y no se vuelve a pintar (perderia el cursor).
  useEffect(() => {
    const el = editor.current;
    if (!enEditor || !el || el.dataset.texto === texto) return;
    el.innerHTML = markdownAHtml(texto);
    el.dataset.texto = texto;
  }, [enEditor, texto, abierto]);
  const listo = !!texto.trim() && (nota || (para.length > 0 && !!asunto.trim())) && !enviando;
  const mandar = async () => {
    if (!listo) return;
    setEnviando(true);
    setError('');
    try {
      await enviar({ modo: sel.modo, para, cc, cco: ccoNoDisponible ? [] : cco, texto, adjuntos, base: sel.base, asunto: asunto.trim() });
      if (conversacion) borrarBorradorRespuesta(dueno, conversacion);
      setTexto('');
      setVista(false);
      setAdjuntos([]);
      setAbierto(false);
    } catch (e) {
      // Lo escrito se queda (y sigue guardado): se puede reintentar.
      setError((e as Error).message || 'No se pudo enviar.');
    } finally {
      setEnviando(false);
    }
  };
  const agregarArchivos = (fs: FileList | null) => {
    const nuevos = [...(fs ?? [])];
    const grande = nuevos.find((f) => f.size > MAX_MB * 1024 * 1024);
    if (grande) toast(`${grande.name} pesa más de ${MAX_MB} MB.`, 'error');
    setAdjuntos((a) => [...a, ...nuevos.filter((f) => f.size <= MAX_MB * 1024 * 1024)]);
  };
  const abrirCon = (m: ModoRespuesta) => {
    setSel({ ...sel, modo: m });
    setAbierto(true);
  };
  const fijarAlto = (px: number | null) => {
    const v = px == null ? null : Math.round(Math.min(Math.max(px, MIN_ALTO), maxAlto()));
    setAlto(v);
    writeJSON(CLAVE_ALTO, v);
  };
  // Agarradera: arrastrar hacia arriba agranda el texto, hacia abajo lo achica.
  const arrastrar = (e: React.PointerEvent) => {
    const c = caja.current;
    if (!c) return;
    e.preventDefault();
    const y0 = e.clientY;
    const h0 = c.offsetHeight;
    const mover = (ev: PointerEvent) => fijarAlto(h0 + (y0 - ev.clientY));
    const soltar = () => {
      window.removeEventListener('pointermove', mover);
      window.removeEventListener('pointerup', soltar);
    };
    window.addEventListener('pointermove', mover);
    window.addEventListener('pointerup', soltar);
  };
  const modos = ['responder', 'todos', 'reenviar', ...(conNota ? ['nota'] : [])] as ModoRespuesta[];
  const hayBorrador = !!texto.trim();

  if (!abierto)
    return (
      <div className="resp resp-mini" ref={tarjeta}>
        <button type="button" className={`resp-abrir ${hayBorrador ? 'con-borrador' : ''}`} onClick={() => abrirCon(sel.modo === 'nota' ? 'nota' : 'responder')}>
          <Icon name="reply" size={14} /> {hayBorrador ? 'Seguir escribiendo la respuesta…' : `Responder a ${sel.base?.deNombre || sel.base?.de || para[0] || 'el cliente'}…`}
          {hayBorrador ? <span className="resp-punto" aria-label="Hay un borrador guardado" /> : null}
        </button>
        <button type="button" className="btn sm" onClick={() => abrirCon('todos')}>
          <Icon name="users" /> A todos
        </button>
        <button type="button" className="btn sm" onClick={() => abrirCon('reenviar')}>
          <Icon name="send" /> Reenviar
        </button>
        {conNota ? (
          <button type="button" className="btn sm" onClick={() => abrirCon('nota')}>
            <Icon name="note" /> Nota
          </button>
        ) : null}
      </div>
    );

  return (
    <div ref={tarjeta} className={`resp resp-flot ${nota ? 'nota' : ''}`} role="region" aria-label="Respuesta" onKeyDown={(e) => e.key === 'Escape' && setAbierto(false)}>
      <div
        className="resp-agarre"
        role="separator"
        aria-orientation="horizontal"
        aria-label="Cambiar el alto del cuadro (doble clic: automático)"
        tabIndex={0}
        onPointerDown={arrastrar}
        onDoubleClick={() => fijarAlto(null)}
        onKeyDown={(e) => {
          const h = caja.current?.offsetHeight ?? MIN_ALTO;
          if (e.key === 'ArrowUp') (e.preventDefault(), fijarAlto(h + 40));
          if (e.key === 'ArrowDown') (e.preventDefault(), fijarAlto(h - 40));
        }}
        title="Arrastra para cambiar el alto · doble clic: automático"
      >
        <span />
      </div>
      <div className="resp-h">
        <div className="seg" role="group" aria-label="Tipo de respuesta">
          {modos.map((m) => (
            <button key={m} type="button" aria-pressed={sel.modo === m} onClick={() => setSel({ ...sel, modo: m })}>
              {NOMBRE_MODO[m]}
            </button>
          ))}
        </div>
        {nota ? (
          <span className="note resp-aviso">
            <Icon name="lock" size={12} /> {avisoNota}
          </span>
        ) : null}
        <button type="button" className="btn sm ghost resp-cerrar" onClick={() => setAbierto(false)} title="Minimizar (se guarda lo escrito)" aria-label="Minimizar la respuesta">
          <Icon name="chevDown" />
        </button>
      </div>
      {!nota ? (
        <div className="resp-campos">
          <div className="resp-fila">
            <CampoCorreos etiqueta="Para" lista={para} cambiar={setPara} />
            {verCc ? null : (
              <button type="button" className="linkish resp-cc" onClick={() => setVerCc(true)}>
                + CC / CCO
              </button>
            )}
          </div>
          {verCc ? <CampoCorreos etiqueta="CC" lista={cc} cambiar={setCc} /> : null}
          {verCc ? <CampoCorreos etiqueta="CCO" lista={cco} cambiar={setCco} desactivado={!!ccoNoDisponible} aviso={ccoNoDisponible} /> : null}
          {verCc && cco.length && avisoCco && !ccoNoDisponible ? <div className="resp-cco-aviso">{avisoCco}</div> : null}
          <label className="resp-dest resp-asunto">
            <span className="resp-et">Asunto</span>
            <input value={asunto} onChange={(e) => setAsunto(e.target.value)} aria-label="Asunto del correo" placeholder="Asunto del correo" />
          </label>
        </div>
      ) : null}
      {enEditor ? (
        <div
          ref={editor}
          className="resp-vista"
          contentEditable
          suppressContentEditableWarning
          role="textbox"
          aria-multiline="true"
          aria-label="Respuesta con formato"
          onInput={(e) => {
            const md = htmlAMarkdown(e.currentTarget);
            e.currentTarget.dataset.texto = md;
            setTexto(md);
            if (error) setError('');
          }}
          onKeyDown={(e) => (e.ctrlKey || e.metaKey) && e.key === 'Enter' && (e.preventDefault(), void mandar())}
        />
      ) : null}
      <textarea
        hidden={enEditor}
        ref={caja}
        value={texto}
        onChange={(e) => (setTexto(e.target.value), error && setError(''))}
        onKeyDown={(e) => (e.ctrlKey || e.metaKey) && e.key === 'Enter' && (e.preventDefault(), void mandar())}
        placeholder={nota ? 'Nota interna…' : sel.modo === 'reenviar' ? 'Agrega un mensaje (se adjunta el correo reenviado)…' : 'Escribe tu respuesta… (Ctrl+Enter envía, Esc minimiza)'}
        aria-label="Respuesta"
      />
      {adjuntos.length ? (
        <div className="resp-adj">
          {adjuntos.map((f, i) => (
            <span key={`${f.name}-${i}`} className="chip">
              <Icon name="paperclip" size={12} /> {f.name}
              <button type="button" aria-label={`Quitar ${f.name}`} onClick={() => setAdjuntos((a) => a.filter((_, j) => j !== i))}>
                <Icon name="x" size={11} />
              </button>
            </span>
          ))}
        </div>
      ) : null}
      {error ? (
        <p className="resp-error" role="alert">
          <Icon name="alert" size={14} /> No se envió: {error} Lo que escribiste sigue aquí; puedes reintentar.
        </p>
      ) : null}
      <div className="resp-pie">
        <TextoRapido datos={datos} onElegir={(t) => setTexto((x) => (x ? `${x.replace(/\s+$/, '')}\n${t}` : t))} />
        <InsertarArticulo onElegir={(t) => setTexto((x) => (x.trim() ? `${x.replace(/\s+$/, '')}\n\n${t}` : t))} />
        {nota ? null : (
          <button type="button" className="btn sm ghost" onClick={() => archivo.current?.click()} title="Adjuntar archivos">
            <Icon name="paperclip" /> Adjuntar
          </button>
        )}
        {!nota && (conFormato || vista) ? (
          <button
            type="button"
            className="btn sm ghost"
            onClick={() => setVista(!vista)}
            title={vista ? 'Ver el texto sin formato (con ** y |)' : 'Ver y editar como le llega al cliente: negritas, listas y tablas'}
          >
            <Icon name={vista ? 'edit' : 'eye'} /> {vista ? 'Ver texto' : 'Ver con formato'}
          </button>
        ) : null}
        <input ref={archivo} type="file" multiple hidden onChange={(e) => (agregarArchivos(e.target.files), (e.target.value = ''))} />
        <span className="note resp-desde" title={desde}>
          {nota ? '' : desde}
          {sel.base && !nota
            ? ` · ${sel.modo === 'reenviar' ? 'reenviando' : 'respondiendo a'} el correo de ${sel.base.deNombre || sel.base.de} del ${new Date(sel.base.fecha).toLocaleDateString('es-CO', { day: 'numeric', month: 'short' })}`
            : ''}
        </span>
        <button className="btn primary" onClick={() => void mandar()} disabled={!listo}>
          <Icon name={nota ? 'note' : sel.modo === 'reenviar' ? 'send' : 'reply'} /> {enviando ? 'Enviando…' : nota ? 'Guardar nota' : NOMBRE_MODO[sel.modo]}
        </button>
      </div>
    </div>
  );
}

// Archivo -> base64 (para mandarlo a Zammad).
export const enBase64 = (f: File) =>
  new Promise<{ nombre: string; tipo: string; datos: string }>((ok, mal) => {
    const r = new FileReader();
    r.onload = () => ok({ nombre: f.name, tipo: f.type || 'application/octet-stream', datos: String(r.result).split(',')[1] ?? '' });
    r.onerror = () => mal(new Error(`No se pudo leer ${f.name}`));
    r.readAsDataURL(f);
  });
